/* import __COLOCATED_TEMPLATE__ from './unified-content-header.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import { hasFeature } from 'embercom/helpers/has-feature';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';

interface Signature {
  Args: {
    title?: string;
    toggleSidebar?: () => void;
    navbarCollapsed: boolean;
    showPreview?: boolean;
    togglePreview?: () => void;
    previewLocale?: string;
    setPreviewLocale?: (locale: string) => void;
    previewMode?: 'agent' | 'copilot';
    setPreviewMode?: (mode: 'agent' | 'copilot') => void;
    canUseCopilotOnStandalone?: boolean;
    demoType?: string;
    searchState?: UnifiedContentStateService;
    listApi?: KnowledgeHubApi;
    beforeSearch?: () => void;
    showSearch?: boolean;
    showGeneralFilters?: boolean;
    showAudienceFilter?: boolean;
    searchTerm?: string;
    setSearchTerm?: (searchTerm?: string) => void;
    linkedUrls?: string[];
    setLinkedUrls?: (linkedUrls?: string[]) => void;
    titleTerm?: string;
    setTitleTerm?: (titleTerm?: string) => void;
    bodyTerm?: string;
    setBodyTerm?: (bodyTerm?: string) => void;
    urlTerm?: string;
    setUrlTerm?: (urlTerm?: string) => void;
    locale?: string | null;
    setLocale?: (locale: string | null) => void;
    predicates?: any[];
    setPredicates?: (predicates: any[]) => void;
    status?: string | null;
    setStatus?: (status: string | null) => void;
    syncStatus?: string[] | null;
    setSyncStatus?: (syncStatus: string[] | null) => void;
    chatbotState?: string;
    setChatbotState?: (chatbotState?: string) => void;
    copilotState?: string;
    setCopilotState?: (copilotState?: string) => void;
    salesAgentState?: string;
    setSalesAgentState?: (salesAgentState?: string) => void;
    createdBy?: string;
    setCreatedBy?: (createdBy?: string) => void;
    lastUpdatedBy?: string;
    setLastUpdatedBy?: (lastUpdatedBy?: string) => void;
    selectedTypes?: any[];
    setSelectedTypes?: (selectedTypes?: any[]) => void;
    activeFilter?: string;
    setActiveFilter?: (activeFilter?: string) => void;
    filtersApplied?: string[];
    setFiltersApplied?: (filtersApplied: string[]) => void;
    resetSelectedFragments?: () => void;
    tagIds?: any[];
    setTagIds?: (tagIds?: any[]) => void;
    tagOperator?: 'IN' | 'NIN';
    setTagOperator?: (tagOperator: 'IN' | 'NIN') => void;
    collectionIds?: any[];
    setCollectionIds?: (collectionIds?: any[]) => void;
    writtenByIds?: any[];
    setWrittenByIds?: (writtenByIds?: any[]) => void;
    aiContentSegmentIds?: any[];
    setAiContentSegmentIds?: (aiContentSegmentIds?: any[]) => void;
    urlSourceId?: string;
    setUrlSourceId?: (urlSourceId?: string) => void;
    selectedHelpCenterIds?: string[];
    setSelectedHelpCenterIds?: (selectedHelpCenterIds?: string[]) => void;
  };
}

export default class UnifiedContentHeader extends Component<Signature> {
  @service declare appService: AppService;
  @service declare finAgentTypeService: FinAgentTypeService;

  noop = () => {};
  noopString = (_value?: string) => {};
  noopStringNull = (_value: string | null) => {};
  noopStringArray = (_value?: string[]) => {};
  noopPredicateArray = (_value: Array<any>) => {};
  noopEntityTypeArray = (_value?: Array<any>) => {};
  noopMode = (_value: 'agent' | 'copilot') => {};

  get useReactPreview(): boolean {
    return hasFeature('react-preview-panel-content', this.appService) && !this.isStandaloneApp;
  }

  get isStandaloneApp(): boolean {
    return this.appService.app.canUseStandalone;
  }

  get shouldShowPreviewToggle(): boolean {
    return !this.isStandaloneApp && !this.args.showPreview;
  }

  get shouldShowPreviewPanel(): boolean {
    return this.isStandaloneApp || !!this.args.showPreview;
  }

  get closeAction(): (() => void) | undefined {
    return this.isStandaloneApp ? undefined : this.args.togglePreview;
  }

  get searchProps() {
    let state = this.args.searchState;
    return {
      searchTerm: state?.searchTerm ?? this.args.searchTerm,
      setSearchTerm: state?.setSearchTerm ?? this.args.setSearchTerm ?? this.noopString,
      linkedUrls: state?.linkedUrls ?? this.args.linkedUrls,
      setLinkedUrls: state?.setLinkedUrls ?? this.args.setLinkedUrls ?? this.noopStringArray,
      titleTerm: state?.titleTerm ?? this.args.titleTerm,
      setTitleTerm: state?.setTitleTerm ?? this.args.setTitleTerm ?? this.noopString,
      bodyTerm: state?.bodyTerm ?? this.args.bodyTerm,
      setBodyTerm: state?.setBodyTerm ?? this.args.setBodyTerm ?? this.noopString,
      urlTerm: state?.urlTerm ?? this.args.urlTerm,
      setUrlTerm: state?.setUrlTerm ?? this.args.setUrlTerm ?? this.noopString,
    };
  }

  get filterProps() {
    let state = this.args.searchState;
    let props: any = {};

    if (this.args.showGeneralFilters !== false) {
      props = {
        ...props,
        locale: state?.locale ?? this.args.locale ?? null,
        setLocale: state?.setLocale ?? this.args.setLocale ?? this.noopStringNull,
        predicates: state?.predicates ?? this.args.predicates,
        setPredicates: state?.setPredicates ?? this.args.setPredicates ?? this.noopPredicateArray,
        status: state?.status ?? this.args.status ?? null,
        setStatus: state?.setStatus ?? this.args.setStatus ?? this.noopStringNull,
        syncStatus: state?.syncStatus ?? this.args.syncStatus,
        setSyncStatus: state?.setSyncStatus ?? this.args.setSyncStatus,
        chatbotState: state?.chatbotState ?? this.args.chatbotState,
        setChatbotState: state?.setChatbotState ?? this.args.setChatbotState ?? this.noopString,
        copilotState: state?.copilotState ?? this.args.copilotState,
        setCopilotState: state?.setCopilotState ?? this.args.setCopilotState ?? this.noopString,
        salesAgentState: state?.salesAgentState ?? this.args.salesAgentState,
        setSalesAgentState:
          state?.setSalesAgentState ?? this.args.setSalesAgentState ?? this.noopString,
        createdBy: state?.createdBy ?? this.args.createdBy,
        setCreatedBy: state?.setCreatedBy ?? this.args.setCreatedBy ?? this.noopString,
        lastUpdatedBy: state?.lastUpdatedBy ?? this.args.lastUpdatedBy,
        setLastUpdatedBy: state?.setLastUpdatedBy ?? this.args.setLastUpdatedBy ?? this.noopString,
        selectedTypes: state?.selectedTypes ?? this.args.selectedTypes,
        setSelectedTypes:
          state?.setSelectedTypes ?? this.args.setSelectedTypes ?? this.noopEntityTypeArray,
        activeFilter: state?.activeFilter ?? this.args.activeFilter,
        setActiveFilter: state?.setActiveFilter ?? this.args.setActiveFilter ?? this.noopString,
        filtersApplied: state?.filtersApplied ?? this.args.filtersApplied ?? [],
        setFiltersApplied:
          state?.setFiltersApplied ?? this.args.setFiltersApplied ?? this.noopStringArray,
        resetSelectedFragments:
          state?.resetSelectedFragments ?? this.args.resetSelectedFragments ?? this.noop,
        tagIds: state?.tagIds ?? this.args.tagIds,
        setTagIds: state?.setTagIds ?? this.args.setTagIds ?? this.noopEntityTypeArray,
        tagOperator: state?.tagOperator ?? this.args.tagOperator,
        setTagOperator: state?.setTagOperator ?? this.args.setTagOperator,
        collectionIds: state?.collectionIds ?? this.args.collectionIds,
        setCollectionIds:
          state?.setCollectionIds ?? this.args.setCollectionIds ?? this.noopEntityTypeArray,
        writtenByIds: state?.writtenByIds ?? this.args.writtenByIds,
        setWrittenByIds:
          state?.setWrittenByIds ?? this.args.setWrittenByIds ?? this.noopEntityTypeArray,
        urlSourceId: state?.urlSourceId ?? this.args.urlSourceId,
        setUrlSourceId: state?.setUrlSourceId ?? this.args.setUrlSourceId ?? this.noopString,
        selectedHelpCenterIds: state?.selectedHelpCenterIds ?? this.args.selectedHelpCenterIds,
        setSelectedHelpCenterIds:
          state?.setSelectedHelpCenterIds ??
          this.args.setSelectedHelpCenterIds ??
          this.noopStringArray,
      };
    }

    if (this.args.showAudienceFilter !== false) {
      props = {
        ...props,
        aiContentSegmentIds: state?.aiContentSegmentIds ?? this.args.aiContentSegmentIds,
        setAiContentSegmentIds:
          state?.setAiContentSegmentIds ??
          this.args.setAiContentSegmentIds ??
          this.noopEntityTypeArray,
      };
    }

    return props;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::UnifiedContent::UnifiedContentHeader': typeof UnifiedContentHeader;
    'knowledge-hub/unified-content/unified-content-header': typeof UnifiedContentHeader;
  }
}
